import { ref } from 'vue'

function getCsrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content ?? ''
}

function apiHeaders() {
    return {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-CSRF-TOKEN': getCsrfToken(),
    }
}

async function apiFetch(url, options = {}) {
    const response = await fetch(url, {
        ...options,
        headers: {
            ...apiHeaders(),
            ...(options.headers ?? {}),
        },
    })

    if (!response.ok) {
        const text = await response.text()
        let message = `API error ${response.status}`

        try {
            const payload = JSON.parse(text)
            if (Array.isArray(payload.errors) && payload.errors.length > 0) {
                message = payload.errors.join(' ')
            } else if (
                typeof payload.message === 'string' &&
                payload.message !== ''
            ) {
                message = payload.message
            }
        } catch {
            if (text !== '') message = `${message}: ${text}`
        }

        throw new Error(message)
    }

    if (response.status === 204) return null
    return response.json()
}

export function useWorkflowApi() {
    const loading = ref(false)
    const error = ref(null)

    async function run(fn) {
        loading.value = true
        error.value = null
        try {
            return await fn()
        } catch (e) {
            error.value = e.message ?? String(e)
            throw e
        } finally {
            loading.value = false
        }
    }

    function fetchWorkflows() {
        return run(() => apiFetch('/bo/communications/api/v1/workflows'))
    }

    function fetchWorkflow(id) {
        return run(() => apiFetch(`/bo/communications/api/v1/workflows/${id}`))
    }

    function createWorkflow(data) {
        return run(() => apiFetch('/bo/communications/api/v1/workflows', {
            method: 'POST',
            body: JSON.stringify(data),
        }))
    }

    function updateWorkflow(id, data) {
        return run(() => apiFetch(`/bo/communications/api/v1/workflows/${id}`, {
            method: 'PUT',
            body: JSON.stringify(data),
        }))
    }

    function deleteWorkflow(id) {
        return run(() => apiFetch(`/bo/communications/api/v1/workflows/${id}`, {
            method: 'DELETE',
        }))
    }

    function saveGraph(id, graphData) {
        return run(() => apiFetch(`/bo/communications/api/v1/workflows/${id}/graph`, {
            method: 'PUT',
            body: JSON.stringify({ graph_data: graphData }),
        }))
    }

    function publishWorkflow(id) {
        return run(() => apiFetch(`/bo/communications/api/v1/workflows/${id}/publish`, {
            method: 'POST',
        }))
    }

    function pauseWorkflow(id) {
        return run(() => apiFetch(`/bo/communications/api/v1/workflows/${id}/pause`, {
            method: 'POST',
        }))
    }

    function resumeWorkflow(id) {
        return run(() => apiFetch(`/bo/communications/api/v1/workflows/${id}/resume`, {
            method: 'POST',
        }))
    }

    return {
        loading,
        error,
        fetchWorkflows,
        fetchWorkflow,
        createWorkflow,
        updateWorkflow,
        deleteWorkflow,
        saveGraph,
        publishWorkflow,
        pauseWorkflow,
        resumeWorkflow,
    }
}
